import { useState, useEffect } from 'react';
import { api } from '../api';
import { toast } from '../components/Toast';

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [drawers, setDrawers] = useState([]);
  const [cache, setCache] = useState([]);
  const [recipeCategories, setRecipeCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newDrawer, setNewDrawer] = useState({ name: '', description: '' });
  const [editDrawer, setEditDrawer] = useState(null);
  const [editCache, setEditCache] = useState(null);
  const [newCat, setNewCat] = useState('');
  const [editCat, setEditCat] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const load = async () => {
    const [s, d, c, rc] = await Promise.all([
      api.get('/settings'),
      api.get('/drawers'),
      api.get('/barcode-cache'),
      api.get('/recipe-categories'),
    ]);
    setSettings(s);
    setDrawers(d);
    setCache(c);
    setRecipeCategories(rc);
  };

  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast('Instellingen opgeslagen ✓');
    } catch {
      toast('Fout bij opslaan', 'error');
    }
    setSaving(false);
  };

  const addDrawer = async () => {
    if (!newDrawer.name.trim()) return;
    await api.post('/drawers', newDrawer);
    setNewDrawer({ name: '', description: '' });
    load();
    toast('Lade toegevoegd ✓');
  };

  const saveDrawer = async (id) => {
    await api.put(`/drawers/${id}`, editDrawer);
    setEditDrawer(null);
    load();
    toast('Lade bijgewerkt ✓');
  };

  const deleteDrawer = async (id, name) => {
    if (!window.confirm(`Lade "${name}" verwijderen? Producten in deze lade verliezen hun locatie.`)) return;
    await api.delete(`/drawers/${id}`);
    load();
    toast('Lade verwijderd');
  };

  const saveCacheEntry = async (barcode) => {
    await api.put(`/barcode-cache/${barcode}`, editCache);
    setEditCache(null);
    load();
    toast('Cache bijgewerkt ✓');
  };

  const deleteCacheEntry = async (barcode, name) => {
    if (!window.confirm(`Cache voor "${name || barcode}" verwijderen?`)) return;
    await api.delete(`/barcode-cache/${barcode}`);
    load();
    toast('Cache verwijderd');
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post('/recipe-categories', { name: newCat.trim() });
      setNewCat('');
      load();
      toast('Categorie toegevoegd ✓');
    } catch {
      toast('Categorie bestaat al', 'error');
    }
  };

  const saveCategory = async (id) => {
    try {
      await api.put(`/recipe-categories/${id}`, { name: editCat.name });
      setEditCat(null);
      load();
      toast('Categorie bijgewerkt ✓');
    } catch {
      toast('Naam bestaat al', 'error');
    }
  };

  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Categorie "${name}" verwijderen?`)) return;
    await api.delete(`/recipe-categories/${id}`);
    load();
    toast('Categorie verwijderd');
  };

  const units = ['stuks', 'g', 'kg', 'ml', 'l', 'portie', 'zak', 'blik', 'doos'];
  const productCategories = ['Vlees', 'Vis', 'Groenten', 'Fruit', 'Zuivel', 'Brood', 'Maaltijd', 'Snacks', 'Drank', 'Overig'];

  const tabs = [
    { id: 'general', label: 'Algemeen' },
    { id: 'drawers', label: 'Lades' },
    { id: 'categories', label: 'Receptcategorieën' },
    { id: 'cache', label: `Cache (${cache.length})` },
  ];

  return (
    <div className="content">
      <div className="filter-row" style={{ marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} className={`filter-pill ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* General */}
      {activeTab === 'general' && (
        <div className="settings-section">
          <h3>Algemene instellingen</h3>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-row-label">
                <strong>Naam diepvriezer</strong>
                <span>Weergavenaam op het dashboard</span>
              </div>
              <input className="form-input" style={{ maxWidth: 240 }} value={settings.freezer_name || ''} onChange={e => setSettings(s => ({ ...s, freezer_name: e.target.value }))} />
            </div>
            <div className="settings-row">
              <div className="settings-row-label">
                <strong>Waarschuwingsdagen</strong>
                <span>Markeer als "bijna verlopen" X dagen voor datum</span>
              </div>
              <input className="form-input" type="number" min="1" max="60" style={{ maxWidth: 100 }} value={settings.expiry_warning_days || '7'} onChange={e => setSettings(s => ({ ...s, expiry_warning_days: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={saveSettings} disabled={saving}>
              {saving ? <span className="loading-spinner" /> : 'Opslaan'}
            </button>
          </div>
        </div>
      )}

      {/* Drawers */}
      {activeTab === 'drawers' && (
        <div className="settings-section">
          <h3>Lades / Compartimenten</h3>
          <div className="drawer-settings-list">
            {drawers.map((d, i) => (
              <div key={d.id} className="drawer-settings-item">
                <span className="drag-handle">⠿</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text3)', width: 20 }}>{i + 1}</span>
                {editDrawer?.id === d.id ? (
                  <>
                    <input className="form-input" value={editDrawer.name} onChange={e => setEditDrawer(x => ({ ...x, name: e.target.value }))} style={{ flex: 1 }} />
                    <input className="form-input" value={editDrawer.description} onChange={e => setEditDrawer(x => ({ ...x, description: e.target.value }))} placeholder="Beschrijving..." style={{ flex: 1 }} />
                    <button className="btn btn-primary btn-sm" onClick={() => saveDrawer(d.id)}>Opslaan</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditDrawer(null)}>✕</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14 }}>{d.name}</strong>
                      {d.description && <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8 }}>{d.description}</span>}
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditDrawer({ ...d })}>✏️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteDrawer(d.id, d.name)}>🗑</button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, background: 'var(--card)', border: '1px dashed var(--border2)', borderRadius: 'var(--radius2)', padding: 16 }}>
            <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 10 }}>+ Nieuwe lade</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <input className="form-input" placeholder="Naam" value={newDrawer.name} onChange={e => setNewDrawer(x => ({ ...x, name: e.target.value }))} onKeyDown={e => e.key === 'Enter' && addDrawer()} style={{ flex: 1, minWidth: 120 }} />
              <input className="form-input" placeholder="Beschrijving (optioneel)" value={newDrawer.description} onChange={e => setNewDrawer(x => ({ ...x, description: e.target.value }))} style={{ flex: 2, minWidth: 120 }} />
              <button className="btn btn-primary" onClick={addDrawer}>Toevoegen</button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe categories */}
      {activeTab === 'categories' && (
        <div className="settings-section">
          <h3>Receptcategorieën</h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16, lineHeight: 1.6 }}>
            Beheer de categorieën die je kunt toekennen aan recepten.
          </p>
          <div className="drawer-settings-list">
            {recipeCategories.map((cat) => (
              <div key={cat.id} className="drawer-settings-item">
                {editCat?.id === cat.id ? (
                  <>
                    <input className="form-input" value={editCat.name} onChange={e => setEditCat(x => ({ ...x, name: e.target.value }))} style={{ flex: 1 }} onKeyDown={e => e.key === 'Enter' && saveCategory(cat.id)} />
                    <button className="btn btn-primary btn-sm" onClick={() => saveCategory(cat.id)}>Opslaan</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditCat(null)}>✕</button>
                  </>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: 14 }}>{cat.name}</strong>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditCat({ ...cat })}>✏️</button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteCategory(cat.id, cat.name)}>🗑</button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, background: 'var(--card)', border: '1px dashed var(--border2)', borderRadius: 'var(--radius2)', padding: 16 }}>
            <div style={{ fontSize: 12, fontFamily: 'var(--mono)', color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 10 }}>+ Nieuwe categorie</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <input
                className="form-input"
                placeholder="Categorienaam"
                value={newCat}
                onChange={e => setNewCat(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCategory()}
                style={{ flex: 1 }}
              />
              <button className="btn btn-primary" onClick={addCategory}>Toevoegen</button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Cache */}
      {activeTab === 'cache' && (
        <div className="settings-section">
          <h3>Barcode cache</h3>
          <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16, lineHeight: 1.6 }}>
            Elke keer dat je een product opslaat met een barcode, worden naam, categorie, eenheid en notities hier opgeslagen.
          </p>
          {cache.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 20px' }}>
              <div className="empty-icon">🏷️</div>
              <p>Nog geen cache. Voeg producten toe met een barcode om de cache te vullen.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {cache.map(entry => (
                <div key={entry.barcode} className="settings-card" style={{ borderRadius: 'var(--radius2)' }}>
                  {editCache?.barcode === entry.barcode ? (
                    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{entry.barcode}</div>
                      <div className="form-row">
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Naam</label>
                          <input className="form-input" value={editCache.name} onChange={e => setEditCache(x => ({ ...x, name: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Categorie</label>
                          <select className="form-select" value={editCache.category} onChange={e => setEditCache(x => ({ ...x, category: e.target.value }))}>
                            <option value="">— Geen —</option>
                            {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Std. hoeveelheid</label>
                          <input className="form-input" type="number" min="0.1" step="0.1" value={editCache.default_quantity} onChange={e => setEditCache(x => ({ ...x, default_quantity: e.target.value }))} inputMode="decimal" />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label className="form-label">Eenheid</label>
                          <select className="form-select" value={editCache.unit} onChange={e => setEditCache(x => ({ ...x, unit: e.target.value }))}>
                            {units.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Notities</label>
                        <input className="form-input" value={editCache.notes} onChange={e => setEditCache(x => ({ ...x, notes: e.target.value }))} />
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditCache(null)}>Annuleren</button>
                        <button className="btn btn-primary btn-sm" onClick={() => saveCacheEntry(entry.barcode)}>Opslaan</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{entry.name || <span style={{ color: 'var(--text3)' }}>Geen naam</span>}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text3)' }}>{entry.barcode}</span>
                          {entry.category && <span className="tag">{entry.category}</span>}
                          <span className="tag">{entry.default_quantity} {entry.unit}</span>
                        </div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditCache({ ...entry })}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteCacheEntry(entry.barcode, entry.name)}>🗑</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'general' && (
        <div className="settings-section">
          <h3>Over</h3>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-row-label">
                <strong>Freezer by Jonas</strong>
                <span>v2.0.0 · Open Food Facts API voor barcode lookup</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
